import React, { Component } from 'react';
import PropTypes from 'prop-types';
import NetWorthChart from '../NetWorthChart/NetWorthChartContainer';
import BalanceSheet from '../Balancesheet/BalanceSheetContainer';
import EarmarkIcons from '../EarmarkIcons/EarmarkIcons';
import { Select } from '../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Text } from '../CommonComponents/OPL/Text/Text';

const markNegativesRed = number => {
  const num = parseInt(number, 10);
  if (num < 0) {
    return <span className="red">{`$${ADV.util.number_format(Math.abs(num))}`}</span>;
  }
  return `$${ADV.util.number_format(num)}`;
};
class NetWorthReportView extends Component {
  constructor(props) {
    super(props);

    this.changeHistory = this.changeHistory.bind(this);
    this.state = {
      showAccounts: false,
      showBalancesheet: true,
    };
  }

  componentDidMount() {
    const { fetchData, clientId, portalName } = this.props;

    document.title = `Net Worth Report | ${portalName}`;

    fetchData(null, clientId);
  }

  componentWillUnmount() {
    const { resetState } = this.props;
    resetState();
  }

  changeHistory(range) {
    const { setRange, clientId } = this.props;
    setRange(range, clientId);
  }

  toggleView(accounts) {
    this.setState({
      showAccounts: accounts,
      showBalancesheet: !accounts,
    });
  }

  render() {
    const {
      clientId,
      is_initialized: isInitialized,
      NetTotalInRed,
      CurrentNetWorth,
      AccountDetailed,
      range,
      hasAccessToLinkAccounts,
      showNetWorth,
      historyOptions,
    } = this.props;

    const { showAccounts, showBalancesheet } = this.state;

    if (isInitialized) {
      const liabilitiesList = AccountDetailed.Liabilities.map(liability => ({
        ...liability,
        Items: liability.Items.map(item => ({
          ...item,
          Value: item.Value * -1,
        })),
      }));

      const masterAccountList = AccountDetailed.Assets.concat(liabilitiesList);

      if (!showNetWorth) {
        return null;
      }

      return (
        <div id="NetWorthReport">
          <header>
            <h2>
              Net Worth{' '}
              <span className={NetTotalInRed ? 'red' : ''}>
                {`$${ADV.util.number_format(Math.abs(CurrentNetWorth))}`}
              </span>
            </h2>

            <div className="view-selection">
              <ul className="tabs chart-type">
                <li
                  id="Balance"
                  className={showBalancesheet ? 'selected' : ''}
                  onClick={() => this.toggleView(false)}
                >
                  <span>Balance Sheet View</span>
                </li>
                <li
                  id="Accounts"
                  className={showAccounts ? 'selected' : ''}
                  onClick={() => this.toggleView(true)}
                >
                  <span>Accounts View</span>
                </li>
              </ul>
            </div>
          </header>

          {showAccounts && (
            <>
              <Box width="200px">
                <Select
                  options={historyOptions}
                  value={
                    range
                      ? historyOptions.find(({ value }) => value === range)
                      : historyOptions.find(({ isDefault }) => isDefault)
                  }
                  onChange={opt => {
                    if (opt) {
                      this.changeHistory(opt.value);
                    }
                  }}
                />
              </Box>

              <section className="chart">
                <section className="NetWorthReport_BarChart">
                  <div className="chart-container">
                    <NetWorthChart clientId={clientId} range={range} />
                  </div>
                </section>
              </section>
            </>
          )}

          <section className="tables">
            {showAccounts && (
              <div id="NetWorthAccountsReport">
                {!!masterAccountList.length && (
                  <section className="accounts_table">
                    <ul className="noselect-highlight NetWorthAccount_list">
                      {masterAccountList.map(account => (
                        <li key={account.Name}>
                          <h3>{account.Name}</h3>
                          <table style={{ background: '#fff' }}>
                            <tr className="labels">
                              {AccountDetailed.Descriptors.map(descriptor => (
                                <td
                                  key={descriptor}
                                  className={descriptor === 'Balance' ? 'text-right' : ''}
                                >
                                  <Text color="neutral.600" fontWeight="600" py="8">
                                    {descriptor}
                                  </Text>
                                </td>
                              ))}
                            </tr>
                            {account.Items.map(item => {
                              return (
                                <tr key={item.Id}>
                                  <td title={item.Name}>
                                    {item.LinkToTransactions && hasAccessToLinkAccounts ? (
                                      <a
                                        onClick={() => {
                                          if (item.IsOrion) {
                                            return ADV.util.Router.go2(
                                              'AccountPerformanceReporting',
                                              {
                                                accountId: item.Id,
                                                clientId,
                                                tabSlug: 'summary',
                                                isApex: item.IsApexOrion || false,
                                              },
                                            );
                                          }
                                          if (item.Id) {
                                            return ADV.util.Router.go2('Transactions', {
                                              accountId: item.Id,
                                              clientId,
                                            });
                                          }
                                        }}
                                      >
                                        {item.Name}
                                      </a>
                                    ) : (
                                      <span>{item.Name}</span>
                                    )}
                                  </td>
                                  <td title={item.Institution}>{item.Institution}</td>
                                  <td>
                                    <EarmarkIcons earmarks={item.Earmarks} />
                                  </td>
                                  <td>{item.Type}</td>
                                  <td className="text-right">{markNegativesRed(item.Value)}</td>
                                </tr>
                              );
                            })}
                          </table>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
            {showBalancesheet && <BalanceSheet clientId={clientId} />}
          </section>
        </div>
      );
    }
    return null;
  }
}

NetWorthReportView.defaultProps = {
  CurrentNetWorth: null,
  AccountDetailed: null,
  historyOptions: [],
};

NetWorthReportView.propTypes = {
  clientId: PropTypes.string.isRequired,
  is_initialized: PropTypes.bool.isRequired,
  NetTotalInRed: PropTypes.bool.isRequired,
  CurrentNetWorth: PropTypes.number,
  AccountDetailed: PropTypes.shape({
    Liabilities: PropTypes.arrayOf(PropTypes.shape({})),
    Assets: PropTypes.arrayOf(PropTypes.shape({})),
    Descriptors: PropTypes.arrayOf(PropTypes.string),
  }),
  range: PropTypes.string.isRequired,
  setRange: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  hasAccessToLinkAccounts: PropTypes.bool.isRequired,
  showNetWorth: PropTypes.bool.isRequired,
  portalName: PropTypes.string.isRequired,
  historyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      isDefault: PropTypes.bool,
    }),
  ),
};

export default NetWorthReportView;
